import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tab,
  Tabs
} from '@mui/material';
import type { ProjectSchemaJSON } from '@takeshape/schema';
import CodeBlock from 'app/common/components/code-block';
import type React from 'react';
import LoaderButton from '../../../common/components/loader-button/index.js';
import { BranchMergeOperation } from '../../actions.js';
import type { BranchMergeState } from '../../types.js';
import { SchemaDiff } from '../schema-diff/index.js';

export const BRANCH_MERGE_TAB_DIFF = 'DIFF';
export const BRANCH_MERGE_TAB_JSON = 'JSON';
export type BranchMergeTab = typeof BRANCH_MERGE_TAB_DIFF | typeof BRANCH_MERGE_TAB_JSON;

export type BranchMergeHookProps = {
  readonly dirty: boolean;
  readonly beforeSchema: ProjectSchemaJSON | null;
  readonly handleClose: () => void;
  readonly handleMerge: () => void;
  readonly tabValue: BranchMergeTab;
  readonly handleTabChange: (event: React.SyntheticEvent, value: BranchMergeTab) => void;
} & BranchMergeState;

export type BranchMergeProps = BranchMergeHookProps;

export const BranchMerge: React.FC<BranchMergeProps> = (props) => {
  const {
    operation,
    open,
    loading,
    inProgress,
    error,
    mergePreview,
    beforeSchema,
    branchName,
    dirty,
    tabValue,
    handleTabChange,
    handleClose,
    handleMerge
  } = props;

  let content;
  let actions;

  if (error) {
    content = <Alert severity="error">{error}</Alert>;
    actions = <Button onClick={handleClose}>Cancel</Button>;
  } else if (loading) {
    content = (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
    actions = null;
  } else {
    content = (
      <>
        {dirty && (
          <Alert severity="warning">Your branch has undeployed changes that will be lost if you proceed.</Alert>
        )}
        <DialogContentText>
          {operation === BranchMergeOperation.Update
            ? `This will update "${branchName}" based on the latest in production.`
            : `This will update production based on the latest in "${branchName}" and delete "${branchName}".`}
        </DialogContentText>
        <DialogContentText>Here is a preview of the updated schema:</DialogContentText>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="wrapped label tabs example">
          <Tab value={BRANCH_MERGE_TAB_DIFF} label="Diff" />
          <Tab value={BRANCH_MERGE_TAB_JSON} label="JSON" />
        </Tabs>
        <Box sx={{ height: 300, overflowY: 'scroll' }}>
          {tabValue === BRANCH_MERGE_TAB_DIFF && (
            <div>{mergePreview && <SchemaDiff before={beforeSchema} after={mergePreview} />}</div>
          )}
          {tabValue === BRANCH_MERGE_TAB_JSON && (
            <CodeBlock code={JSON.stringify(mergePreview, null, 2)} language="json" height="290px" />
          )}
        </Box>
      </>
    );
    actions = (
      <>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        {!loading && (
          <LoaderButton loading={inProgress} variant="contained" onClick={handleMerge}>
            {operation} {branchName}
          </LoaderButton>
        )}
      </>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{operation} Branch</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>{content}</DialogContent>
      <DialogActions>{actions}</DialogActions>
    </Dialog>
  );
};
